import type {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import axios from 'axios';

import { Configuration } from '../enums/Configuration';
import { store } from '../state-management/store';
import {
    addInterceptors,
    urlFiller,
} from './errorUtils';
import type { CustomAxiosInstance } from './types';

let token = '';
let axiosInstance: AxiosInstance;

const headers = { Authorization: '' };

// Retry configuration
interface RetryConfig {
    maxRetries: number;
    baseDelay: number;
    maxDelay: number;
    retryCondition: (error: AxiosError) => boolean;
}

const defaultRetryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    retryCondition: (error: AxiosError) => error.response?.status ? error.response.status >= 500 : false,
};

// Retry condition as long as status is not 200
export const retryConditionOnNot200 = (error: AxiosError) => {
    if (!error.response) {
        return true;
    }
    return error.response.status !== 200;
};

// Retry utility function with exponential backoff
const retryRequest = async <T>(
    requestFn: () => Promise<T>,
    retryConfig: RetryConfig = defaultRetryConfig,
): Promise<T> => {
    const retryConfiguration: RetryConfig = {
        ...defaultRetryConfig,
        ...retryConfig,
    };
    let lastError: AxiosError;

    for (let attempt = 0; attempt <= retryConfiguration.maxRetries; attempt++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error as AxiosError;

            // Don't retry if it's the last attempt or if retry condition is not met
            if (attempt === retryConfig.maxRetries || !retryConfig.retryCondition(lastError)) {
                throw lastError;
            }

            // Calculate delay with exponential backoff
            const delay = Math.min(
                retryConfiguration.baseDelay * Math.pow(2, attempt),
                retryConfiguration.maxDelay,
            );

            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError!;
};

const waitForInit = new Promise((resolve) => {
    const unsubscribe = store.subscribe(() => {
        const {
            auth, config,
        } = store.getState();
        if (config.state === Configuration.APPREADY && !!auth.authToken) {
            updateInstanceWithParameters(auth.authToken);
            resolve(true);
            unsubscribe();
        }
    });
});

store.subscribe(() => {
    const {
        auth, config,
    } = store.getState();
    if (config.state === Configuration.APPREADY && auth.authToken !== token) {
        updateInstanceWithParameters(auth.authToken);
    }
});

const http: CustomAxiosInstance = {
    get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.get(urlFiller(url), config)),
        );
    },
    delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.delete(urlFiller(url), config)),
        );
    },
    head<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.head(urlFiller(url), config)),
        );
    },
    options<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.options(urlFiller(url), config)),
        );
    },
    post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.post(urlFiller(url), data, config)),
        );
    },
    put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig, retryConfig: RetryConfig = defaultRetryConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.put(urlFiller(url), data, config), retryConfig));
    },
    patch<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.patch(urlFiller(url), data, config)),
        );
    },
    CancelToken: axios.CancelToken,
};

/* eslint-enable */

const updateInstanceWithParameters = (authToken: string): void => {
    token = authToken;

    headers.Authorization = `Bearer ${authToken}`;

    axiosInstance = axios.create({
    /* Increase the timeout to 20 seconds, Pipeline kill is taking more than 10 seconds */
        timeout: 0,
        headers: { Authorization: `Bearer ${authToken}` },
    });

    addInterceptors(axiosInstance);

};

export {
    http,
    headers,
};
